var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var TimeField_1;
import React, { Component, Fragment } from 'react';
import isEqual from 'lodash.isequal';
import moment from 'moment';
import { Alignment, ButtonTheme, Placement, SensitiveData } from '@philips-internal/oswald-ui-constants';
import { AwesomeComponent, ComponentType } from '@philips-internal/oswald-ui-core';
import { arrayOf, bool, eventListener, expression, oneOf, oneOfType, string } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { ChangeEvent, FocusEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { getPrettyTimeValue } from '@philips-internal/oswald-ui-model';
import { classNames, enumValues, isContentfulString, isDefined, isFunction, isMobileResolution, isUndefined } from '@philips-internal/oswald-ui-utils';
import { ReactComponent as ClockIcon } from '../../../resources/assets/components/DLS_Clock.svg';
import { ReactComponent as CloseIcon } from '../../../resources/assets/components/DLS_CloseCrossCircle.svg';
import { Button } from '../button/button.js';
import { Popover } from '../popover/popover.js';
import { SlideUpPanel } from '../slide-up-panel/slide-up-panel.js';
import { TimeInput } from './components/time-input.js';
import { TimePicker } from './components/time-picker.js';
import TimeFieldAPI from './time-field-api.js';
import { TimeFieldFormat, TimeFieldMode, TimeFieldPickerIndex, TimeFieldSelectionMode } from './time-field-constants.js';
import { formatTimeNumber, getTimeMode, getValueFromInternalValue, toLocalShortTime } from './time-field-utils.js';
import './time-field.css';
let TimeField = TimeField_1 = class TimeField extends Component {
    constructor() {
        super(...arguments);
        this.timeMode = TimeFieldMode.TWELVE_HOURS;
        this.state = {
            showPicker: false,
        };
        this.getHours = () => {
            const isTwelveHours = this.timeMode === TimeFieldMode.TWELVE_HOURS;
            const length = isTwelveHours ? 12 : 24;
            return Array.from({ length }, (_item, index) => formatTimeNumber(isTwelveHours ? index + 1 : index));
        };
        this.getMinutes = () => {
            return Array.from({ length: 60 }, (_item, index) => formatTimeNumber(index));
        };
        this.onTimeInputChange = (event) => {
            this.updateInternalValue(event.value);
        };
        this.onTimePickerChange = (event) => {
            this.setState(({ internalValue: prevInternalValue }) => {
                const { index, value } = event;
                if (isUndefined(index)) {
                    return {
                        internalValue: value,
                    };
                }
                const newValue = Array.isArray(prevInternalValue) ? [...prevInternalValue] : [];
                newValue.splice(index, 1, value);
                return {
                    internalValue: newValue,
                };
            });
        };
        this.onBlur = () => {
            const { onBlur } = this.props;
            if (isFunction(onBlur)) {
                onBlur(new FocusEvent());
            }
        };
        this.onFocus = (pickerIndex) => {
            const { onFocus, enabled, readOnly } = this.props;
            if (readOnly || !enabled) {
                return;
            }
            if (isFunction(onFocus)) {
                onFocus(new FocusEvent());
            }
            this.openPicker(pickerIndex);
        };
        this.getNowValue = () => {
            const format = this.timeMode === TimeFieldMode.TWELVE_HOURS ? TimeFieldFormat.WITH_PERIOD : TimeFieldFormat.DEFAULT;
            return moment().format(format);
        };
        this.selectNow = () => {
            const { activePickerIndex, internalValue: oldInternalValue } = this.state;
            const isArray = Array.isArray(oldInternalValue);
            const isActiveIndex = isDefined(activePickerIndex);
            if (!isArray) {
                const internalValue = this.getNowValue();
                return this.updateInternalValue(internalValue);
            }
            if (!isActiveIndex) {
                return;
            }
            const newValue = Array.isArray(oldInternalValue) ? [...oldInternalValue] : [];
            const value = this.getNowValue();
            newValue.splice(activePickerIndex, 1, value);
            this.updateInternalValue(newValue);
        };
        this.closePicker = () => {
            this.setState({
                activePickerIndex: undefined,
                showPicker: false,
            });
        };
        this.openPicker = (activePickerIndex) => {
            this.setState({
                activePickerIndex,
                showPicker: true,
            });
        };
        this.applyValue = () => {
            this.closePicker();
            this.fireChange(this.getInternalValue());
        };
        this.updateInternalValue = (internalValue) => {
            this.setState({ internalValue }, this.applyValue);
        };
        this.getFromOrUntilValue = (pickerIndex = 0) => {
            const value = this.getInternalValue();
            if (!Array.isArray(value)) {
                return value;
            }
            return value[pickerIndex];
        };
        this.renderPreviewTitle = (pickerIndex) => {
            const { fromExpression = TimeField_1.defaultProps.fromExpression, interval, untilExpression = TimeField_1.defaultProps.untilExpression, } = this.props;
            if (!interval) {
                return null;
            }
            const expression = pickerIndex === TimeFieldPickerIndex.FROM ? fromExpression : untilExpression;
            return (React.createElement("div", { className: "c-time-input__preview-title" }, MessageService.translate(expression)));
        };
        this.renderTimePicker = (pickerIndex) => {
            const { data, fromExpression = TimeField_1.defaultProps.fromExpression, interval, noValueExpression = TimeField_1.defaultProps.noValueExpression, selectionMode, untilExpression = TimeField_1.defaultProps.untilExpression, } = this.props;
            const { activePickerIndex } = this.state;
            const hasIndex = isDefined(pickerIndex);
            const isActiveIndex = pickerIndex === activePickerIndex;
            const isMobile = isMobileResolution();
            const currentValue = this.getInternalValue();
            if (hasIndex && !isMobile && !isActiveIndex) {
                return null;
            }
            if (hasIndex && isMobile && !isActiveIndex) {
                const onPreviewClick = () => this.setState({ activePickerIndex: pickerIndex });
                const previewClassName = classNames({
                    'c-time-field__picker-preview': true,
                    'c-time-field__picker-preview--from': pickerIndex === TimeFieldPickerIndex.FROM,
                    'c-time-field__picker-preview--until': pickerIndex === TimeFieldPickerIndex.UNTIL,
                });
                const previewValue = currentValue[pickerIndex] || MessageService.translate(noValueExpression);
                return (React.createElement("div", { className: previewClassName, onClick: onPreviewClick },
                    this.renderPreviewTitle(pickerIndex),
                    React.createElement("div", { className: "c-time-field__picker-content" }, previewValue)));
            }
            return (React.createElement(TimePicker, { data: data, fromExpression: fromExpression, hoursArray: this.getHours(), index: pickerIndex, interval: interval, minutesArray: this.getMinutes(), onChange: this.onTimePickerChange, selectionMode: selectionMode, time: this.getFromOrUntilValue(pickerIndex), timeMode: this.timeMode, untilExpression: untilExpression }));
        };
        this.clearInput = (pickerIndex) => {
            const { internalValue } = this.state;
            const isArray = Array.isArray(internalValue);
            if (isArray && isDefined(pickerIndex)) {
                const newInternalValue = [...internalValue];
                newInternalValue[pickerIndex] = '';
                const allEmptyValues = newInternalValue.every(value => !isContentfulString(value));
                return this.updateInternalValue(allEmptyValues ? [] : newInternalValue);
            }
            return this.updateInternalValue('');
        };
    }
    /**
     * Prettifies a given value.
     *
     * @param properties - Properties to generate pretty value.
     */
    static formatPrettyValue(properties) {
        const { value } = properties;
        if (!value) {
            return '';
        }
        return getPrettyTimeValue(value);
    }
    componentDidMount() {
        this.timeMode = getTimeMode();
    }
    componentDidUpdate(prevProps) {
        const { interval, selectionMode, value = TimeField_1.defaultProps.value } = this.props;
        const { interval: prevInterval, selectionMode: prevSelectionMode, value: prevValue } = prevProps;
        const { internalValue } = this.state;
        const currentValue = getValueFromInternalValue(internalValue);
        if (!isEqual(value, prevValue) && !isEqual(value, currentValue)) {
            const isArray = Array.isArray(value);
            if (isArray) {
                const formattedValues = value.map(toLocalShortTime);
                const newInternalValue = interval ? formattedValues : formattedValues[0];
                return this.updateInternalValue(newInternalValue);
            }
            const formattedValue = toLocalShortTime(value);
            const newInternalValue = interval ? [formattedValue, ''] : formattedValue;
            return this.updateInternalValue(newInternalValue);
        }
        const intervalChanged = interval !== prevInterval;
        const selectionModeChanged = selectionMode !== prevSelectionMode;
        const shouldClearValue = Boolean(internalValue) && (intervalChanged || selectionModeChanged);
        if (shouldClearValue) {
            const newInternalValue = interval ? [] : '';
            return this.updateInternalValue(newInternalValue);
        }
    }
    fireChange(internalValue) {
        const { onChange, value: oldValue } = this.props;
        const value = getValueFromInternalValue(internalValue);
        if (isFunction(onChange)) {
            onChange(new ChangeEvent({
                oldValue: oldValue,
                prettyValue: TimeField_1.formatPrettyValue({ value: internalValue }),
                value,
            }));
        }
    }
    getInternalValue() {
        const { internalValue } = this.state;
        if (!internalValue) {
            return '';
        }
        return internalValue;
    }
    onClockIconClick(pickerIndex) {
        const { enabled, readOnly } = this.props;
        if (readOnly || !enabled) {
            return;
        }
        this.openPicker(pickerIndex);
    }
    getExternalActions(pickerIndex) {
        const { enabled, readOnly } = this.props;
        if (readOnly || !enabled) {
            return null;
        }
        return (React.createElement("div", { className: "c-time-input__actions" },
            this.renderClearIcon(pickerIndex),
            React.createElement("div", { className: "c-time-input__clock", onClick: () => this.onClockIconClick(pickerIndex), role: "button", tabIndex: 0 },
                React.createElement(ClockIcon, null))));
    }
    renderClearIcon(pickerIndex) {
        const { showClearAction = TimeField_1.defaultProps.showClearAction, value = TimeField_1.defaultProps.value, } = this.props;
        const isArray = Array.isArray(value);
        const isEmptyArray = isArray && !value.length;
        const inputValue = isDefined(pickerIndex) ? value[pickerIndex] : value;
        if (isEmptyArray || !inputValue || !showClearAction) {
            return null;
        }
        return (React.createElement("div", { className: "c-time-input__clear", onClick: () => this.clearInput(pickerIndex), role: "button", tabIndex: 0 },
            React.createElement(CloseIcon, null)));
    }
    getApplyExpression() {
        const { applyExpression = TimeField_1.defaultProps.applyExpression, okExpression = TimeField_1.defaultProps.okExpression, selectionMode, } = this.props;
        if (isMobileResolution() || selectionMode === TimeFieldSelectionMode.PREDEFINED) {
            return applyExpression;
        }
        return okExpression;
    }
    renderPopoverFooter() {
        const { nowExpression = TimeField_1.defaultProps.nowExpression, selectionMode } = this.props;
        const isFreeSelection = selectionMode === TimeFieldSelectionMode.FREE;
        const theme = isFreeSelection ? ButtonTheme.QUIET_EMPHASIS : ButtonTheme.SECONDARY;
        const isTwelveHours = this.timeMode === TimeFieldMode.TWELVE_HOURS;
        const className = classNames({
            'c-time-picker__popover-footer': true,
            'c-time-picker__popover-footer--full-width': !isFreeSelection || !isTwelveHours,
        });
        return (React.createElement("div", { className: className },
            React.createElement(Button, { onClick: this.selectNow, theme: theme, value: nowExpression }),
            React.createElement(Button, { onClick: this.applyValue, value: this.getApplyExpression() })));
    }
    renderPickerContent() {
        const { interval } = this.props;
        if (!interval) {
            return this.renderTimePicker();
        }
        return (React.createElement(Fragment, null,
            this.renderTimePicker(TimeFieldPickerIndex.FROM),
            this.renderTimePicker(TimeFieldPickerIndex.UNTIL)));
    }
    renderPickerTimeInput() {
        const { interval } = this.props;
        if (!interval) {
            return this.renderTimeInput();
        }
        return (React.createElement(Fragment, null,
            this.renderTimeInput(TimeFieldPickerIndex.FROM),
            this.renderTimeInput(TimeFieldPickerIndex.UNTIL)));
    }
    renderPopover() {
        const { showPicker } = this.state;
        return (React.createElement(Popover, { alignment: Alignment.LEFT, matchTargetWidth: true, onClose: this.closePicker, open: showPicker, padding: 0, placement: Placement.BOTTOM, showArrow: false },
            React.createElement("div", { className: "c-time-field__picker-input-wrapper" }, this.renderPickerTimeInput()),
            React.createElement(Popover.Content, null,
                React.createElement("div", { className: "c-time-field__popover-content-wrapper" },
                    this.renderPickerContent(),
                    this.renderPopoverFooter()))));
    }
    renderSlideUpPanelHeaderContent() {
        const { noValueExpression = TimeField_1.defaultProps.noValueExpression } = this.props;
        const value = this.getInternalValue();
        const isInvalidValue = !value || !value.length;
        const titleContent = isInvalidValue
            ? MessageService.translate(noValueExpression)
            : TimeField_1.formatPrettyValue({ value });
        return (React.createElement("div", { className: "c-time-field__slide-up-panel-header-title" }, titleContent));
    }
    renderSlideUpPanel() {
        const { interval } = this.props;
        const { showPicker } = this.state;
        const className = classNames({
            'c-time-field__slide-up-panel-content': true,
            'c-time-field__slide-up-panel-content--interval': Boolean(interval),
        });
        return (React.createElement(Fragment, null,
            this.renderPickerTimeInput(),
            React.createElement(SlideUpPanel, { closeable: true, onClose: this.closePicker, open: showPicker },
                React.createElement(SlideUpPanel.Header, null, this.renderSlideUpPanelHeaderContent()),
                React.createElement(SlideUpPanel.Content, null,
                    React.createElement("div", { className: className }, this.renderPickerContent())),
                React.createElement(SlideUpPanel.Footer, null, this.renderSlideUpPanelFooter()))));
    }
    renderSlideUpPanelFooter() {
        const { nowExpression } = this.props;
        return (React.createElement("div", { className: "c-time-field__slide-up-panel-footer" },
            React.createElement(Button, { onClick: this.selectNow, theme: ButtonTheme.SECONDARY, value: nowExpression }),
            React.createElement(Button, { onClick: this.applyValue, value: this.getApplyExpression() })));
    }
    renderTimeInput(pickerIndex) {
        const { enabled = TimeField_1.defaultProps.enabled, interval, noValueExpression, readOnly, sensitiveData, strikethrough, valid } = this.props;
        const time = this.getFromOrUntilValue(pickerIndex);
        return (React.createElement(TimeInput, { enabled: enabled, externalAction: this.getExternalActions(pickerIndex), index: pickerIndex, interval: interval, onBlur: this.onBlur, onChange: this.onTimeInputChange, onFocus: () => this.onFocus(pickerIndex), placeholder: noValueExpression, readOnly: readOnly, sensitiveData: sensitiveData, strikethrough: strikethrough, time: time, valid: valid }));
    }
    render() {
        const { interval = TimeField_1.defaultProps.interval } = this.props;
        const className = classNames({
            'c-time-field__container': true,
            'c-time-field__container--interval': interval,
        });
        return (React.createElement("div", { className: className }, isMobileResolution()
            ? this.renderSlideUpPanel()
            : this.renderPopover()));
    }
};
TimeField.propTypes = {
    applyExpression: expression,
    data: arrayOf(string).notEnumerable(),
    enabled: bool,
    fromExpression: expression,
    interval: bool,
    noValueExpression: expression,
    nowExpression: expression,
    okExpression: expression,
    onBlur: eventListener,
    onChange: eventListener,
    onFocus: eventListener,
    readOnly: bool,
    selectionMode: oneOf(enumValues(TimeFieldSelectionMode)),
    sensitiveData: oneOf(enumValues(SensitiveData)),
    showClearAction: bool,
    strikethrough: bool,
    timeMode: oneOf(enumValues(TimeFieldMode)),
    untilExpression: expression,
    uuid: string,
    valid: bool,
    value: oneOfType([string, arrayOf(string)]),
};
TimeField.defaultProps = {
    applyExpression: 'tws.time-field.apply',
    data: [],
    enabled: true,
    fromExpression: 'tws.time-field.from',
    interval: false,
    noValueExpression: 'tws.time-field.placeholder',
    nowExpression: 'tws.time-field.now',
    okExpression: 'tws.time-field.ok',
    selectionMode: TimeFieldSelectionMode.FREE,
    sensitiveData: SensitiveData.NONE,
    showClearAction: true,
    timeMode: TimeFieldMode.TWELVE_HOURS,
    untilExpression: 'tws.time-field.until',
    valid: true,
    value: '',
};
TimeField = TimeField_1 = __decorate([
    AwesomeComponent({ api: TimeFieldAPI, type: ComponentType.DATA_ENTRY })
], TimeField);
export default TimeField;
//# sourceMappingURL=time-field.js.map